import { useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

export default function AtletaSearch() {
  const [cognome, setCognome] = useState("");
  const [nome, setNome] = useState("");
  const [risultati, setRisultati] = useState([]);
  const [atleta, setAtleta] = useState(null);
  const [profile, setProfile] = useState(null);
  const [atletaRecords, setAtletaRecords] = useState([]);
  const [recFilters, setRecFilters] = useState({ evento: "", classe: "", vasca: "", tipo: "", categoria: "" });
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState("");
  const [searchError, setSearchError] = useState(null);
  const [searched, setSearched] = useState(false);

  const cerca = async () => {
    if (!cognome && !nome) return;
    setLoading(true);
    setAtleta(null);
    setProfile(null);
    setSearchError(null);
    setSearched(false);
    try {
      const r = await axios.get(`${API}/atleta/search`, { params: { cognome, nome } });
      setRisultati(r.data);
      setSearched(true);
    } catch {
      setSearchError("Errore nella ricerca. Verifica la connessione e riprova.");
      setRisultati([]);
    }
    setLoading(false);
  };

  const selezionaAtleta = async (nomeAtleta) => {
    setAtleta(nomeAtleta);
    setProfile(null);
    setAtletaRecords([]);
    setRecFilters({ evento: "", classe: "", vasca: "", tipo: "", categoria: "" });
    setProfileLoading(true);
    setLoadingStage("Ricerca nel database...");

    // Fetch records dal nostro DB in parallelo (non dipendono dal profilo natatoria.com)
    axios.get(`${API}/atleta/records`, { params: { nome_atleta: nomeAtleta } })
      .then(res => setAtletaRecords(res.data))
      .catch(() => {});

    const parti = nomeAtleta.split(" ");
    const cog = parti[0] || "";
    const nom = parti.slice(1).join(" ") || "";

    try {
      // 1. Cerca prima nel nostro DB
      const dbRes = await axios.get(`${API}/atleta/id_from_db`, { params: { nome_atleta: nomeAtleta } });
      let id = dbRes.data?.id_atleta;

      // 2. Se non nel DB, prova Playwright
      if (!id) {
        setLoadingStage("Connessione a natatoria.com...");
        const search = await axios.get(`${API}/atleta/find_id`, { params: { cognome: cog, nome: nom } });
        id = search.data?.atleti?.[0]?.id_atleta || null;
      }

      if (id) {
        setLoadingStage("Recupero profilo...");
        const r = await axios.get(`${API}/atleta/profile`, { params: { id_atleta: id } });
        setProfile(r.data);
      } else {
        setProfile({ error: "Profilo non disponibile su natatoria.com." });
      }
    } catch {
      setProfile({ error: "Impossibile caricare il profilo. Verifica la connessione e riprova." });
    }
    setProfileLoading(false);
    setLoadingStage("");
  };

  const cancelProfileLoading = () => {
    setProfileLoading(false);
    setLoadingStage("");
  };

  return (
    <div className="atleta-section">
      <h2>Ricerca Atleta</h2>

      <div className="atleta-search-form" role="search">
        <input
          placeholder="Cognome (min. 3 lettere)"
          value={cognome}
          onChange={e => setCognome(e.target.value)}
          onKeyDown={e => e.key === "Enter" && cerca()}
          aria-label="Cognome atleta"
        />
        <input
          placeholder="Nome (opzionale)"
          value={nome}
          onChange={e => setNome(e.target.value)}
          onKeyDown={e => e.key === "Enter" && cerca()}
          aria-label="Nome atleta"
        />
        <button onClick={cerca} disabled={loading}>
          {loading ? "Cerco..." : "Cerca"}
        </button>
      </div>

      {/* Errore ricerca */}
      {searchError && (
        <div className="error-message" role="alert">
          <span>{searchError}</span>
          <button className="retry-btn" onClick={cerca}>Riprova</button>
        </div>
      )}

      {/* Stato vuoto dopo ricerca */}
      {searched && risultati.length === 0 && !searchError && (
        <div className="empty-state" role="status">
          <p>Nessun atleta trovato per &ldquo;{cognome}{nome ? ` ${nome}` : ""}&rdquo;</p>
          <p className="empty-state-hint">Prova con un cognome diverso o verifica l'ortografia.</p>
        </div>
      )}

      {/* Risultati ricerca */}
      {risultati.length > 0 && (
        <div className="risultati-lista">
          <p className="results-info" aria-live="polite">{risultati.length} atleti trovati nei record FINP</p>
          <div className="atleti-grid">
            {risultati.map(r => (
              <div
                key={r.nome_atleta}
                className={`atleta-card ${atleta === r.nome_atleta ? "selected" : ""}`}
                onClick={() => selezionaAtleta(r.nome_atleta)}
                onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); selezionaAtleta(r.nome_atleta); } }}
                tabIndex={0}
                role="button"
                aria-pressed={atleta === r.nome_atleta}
              >
                <div className="atleta-name">{r.nome_atleta}</div>
                <div className="atleta-meta">
                  {r.anno_nascita && <span>{r.anno_nascita}</span>}
                  {r.nazione && <span> · {r.nazione}</span>}
                  <span> · {r.num_records} record FINP</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sezione profilo completo */}
      {atleta && (
        <div className="atleta-detail">

          {profileLoading && (
            <div className="profile-loading" role="status" aria-live="polite">
              <div className="profile-loading-spinner" aria-hidden="true"></div>
              <span>{loadingStage}</span>
              <button className="profile-loading-cancel" onClick={cancelProfileLoading}>
                Annulla
              </button>
            </div>
          )}

          {/* Profilo completo da natatoria.com */}
          {profile && !profile.error && (
            <div className="profile-full">

              {/* Info base */}
              <div className="profile-card">
                <h4>Scheda Atleta</h4>
                <div className="profile-info-grid">
                  <div><label>Codice</label><span>{profile.codice}</span></div>
                  <div><label>Anno nascita</label><span>{profile.anno_nascita}</span></div>
                  <div><label>Società</label><span>{profile.societa}</span></div>
                  <div><label>Categoria</label><span>{profile.categoria}</span></div>
                  <div><label>Agonista</label><span>{profile.agonista}</span></div>
                </div>
              </div>

              {/* Record FINP */}
              {atletaRecords.length > 0 && (() => {
                const setRF = (k, v) => setRecFilters(f => ({ ...f, [k]: v }));
                const uniq = (key) => [...new Set(atletaRecords.map(r => r[key]))].sort();
                const filtrati = atletaRecords.filter(r =>
                  (!recFilters.evento    || r.evento     === recFilters.evento) &&
                  (!recFilters.classe    || r.classe     === recFilters.classe) &&
                  (!recFilters.vasca     || String(r.vasca) === recFilters.vasca) &&
                  (!recFilters.tipo      || r.tipo_record === recFilters.tipo) &&
                  (!recFilters.categoria || r.categoria  === recFilters.categoria)
                );
                const hasFilter = Object.values(recFilters).some(v => v !== "");
                return (
                  <div className="profile-card">
                    <h4>
                      Record FINP ({hasFilter ? `${filtrati.length} di ${atletaRecords.length}` : atletaRecords.length})
                    </h4>
                    <div className="rec-filters">
                      <select value={recFilters.evento} onChange={e => setRF("evento", e.target.value)} aria-label="Filtra per specialità">
                        <option value="">Tutte le specialità</option>
                        {uniq("evento").map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                      <select value={recFilters.classe} onChange={e => setRF("classe", e.target.value)} aria-label="Filtra per classe">
                        <option value="">Tutte le classi</option>
                        {uniq("classe").map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                      <select value={recFilters.vasca} onChange={e => setRF("vasca", e.target.value)} aria-label="Filtra per vasca">
                        <option value="">Tutte le vasche</option>
                        {uniq("vasca").map(v => <option key={v} value={String(v)}>{v}m</option>)}
                      </select>
                      <select value={recFilters.tipo} onChange={e => setRF("tipo", e.target.value)} aria-label="Filtra per tipo record">
                        <option value="">Tutti i tipi</option>
                        {uniq("tipo_record").map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                      <select value={recFilters.categoria} onChange={e => setRF("categoria", e.target.value)} aria-label="Filtra per categoria">
                        <option value="">Tutte le categorie</option>
                        {uniq("categoria").map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                      {hasFilter && (
                        <button className="rec-filters-reset" onClick={() => setRecFilters({ evento: "", classe: "", vasca: "", tipo: "", categoria: "" })}>
                          Azzera
                        </button>
                      )}
                    </div>
                    <div className="table-wrapper">
                      <table>
                        <thead>
                          <tr>
                            <th>Specialità</th>
                            <th>Classe</th>
                            <th>Tempo</th>
                            <th>Data</th>
                            <th>Luogo</th>
                            <th>Vasca</th>
                            <th>Tipo</th>
                            <th>Categoria</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtrati.map(r => (
                            <tr key={r.id}>
                              <td>{r.evento}</td>
                              <td><span className="badge" title={`Classe ${r.classe} — classificazione funzionale FINP`}>{r.classe}</span></td>
                              <td className="tempo">{r.tempo}</td>
                              <td>{r.data_record}</td>
                              <td>{r.luogo}</td>
                              <td>{r.vasca}m</td>
                              <td><span
                                className={`tipo tipo-${r.tipo_record}`}
                                title={r.tipo_record === "ITA" ? "Record Italiano" : r.tipo_record === "EUR" ? "Record Europeo" : "Record Mondiale"}
                              >{r.tipo_record}</span></td>
                              <td>{r.categoria}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* Classificazioni FINP */}
              {profile.classi?.length > 0 && (
                <div className="profile-card">
                  <h4>FINP Classi Sportive</h4>
                  <div className="table-wrapper">
                    <table>
                      <thead>
                        <tr>
                          <th></th>
                          <th title="Classe S — stile libero, dorso, farfalla">S</th>
                          <th title="Classe SM — misti">SM</th>
                          <th title="Classe SB — rana">SB</th>
                        </tr>
                      </thead>
                      <tbody>
                        {profile.classi.map((c, i) => (
                          <tr key={i}>
                            <td><strong>{c.campo}</strong></td>
                            <td>{c.S}</td>
                            <td>{c.SM}</td>
                            <td>{c.SB}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Tempi Migliori */}
              {profile.tempi_migliori?.length > 0 && (
                <div className="profile-card">
                  <h4>Tempi Migliori</h4>
                  <div className="table-wrapper">
                    <table>
                      <thead>
                        <tr><th>Specialità</th><th>Vasca</th><th>Cron</th><th>Manifestazione</th><th>Data</th><th>Tempo</th></tr>
                      </thead>
                      <tbody>
                        {profile.tempi_migliori.map((t, i) => (
                          <tr key={i}>
                            <td>{t.specialita}</td>
                            <td>{t.vasca}m</td>
                            <td>{t.cron}</td>
                            <td>{t.manifestazione}</td>
                            <td>{t.data}</td>
                            <td className="tempo">{t.tempo}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Statistiche per Specialità */}
              {profile.statistiche?.length > 0 && (
                <div className="profile-card">
                  <h4>Statistiche per Specialità</h4>
                  {profile.statistiche.map((spec, i) => (
                    <div key={i} style={{ marginBottom: 20 }}>
                      {spec.specialita && (
                        <div className="spec-title">{spec.specialita}</div>
                      )}
                      <div className="table-wrapper">
                        <table>
                          <thead>
                            <tr><th>Data</th><th>Manifestazione</th><th>Vasca</th><th>Cron</th><th>Tempo</th></tr>
                          </thead>
                          <tbody>
                            {spec.gare.map((g, j) => (
                              <tr key={j}>
                                <td>{g.data}</td>
                                <td>{g.manifestazione}</td>
                                <td>{g.vasca}m</td>
                                <td>{g.cron}</td>
                                <td className="tempo">{g.tempo}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Gare Disputate */}
              {profile.gare_disputate?.length > 0 && (() => {
                const grouped = [];
                let current = null;
                profile.gare_disputate.forEach(g => {
                  if (!current || current.evento !== g.evento) {
                    current = { evento: g.evento, gare: [] };
                    grouped.push(current);
                  }
                  current.gare.push(g);
                });
                return (
                  <div className="profile-card">
                    <h4>Gare Disputate</h4>
                    {grouped.map((group, i) => (
                      <div key={i} style={{ marginBottom: 20 }}>
                        <div className="spec-title">{group.evento}</div>
                        <div className="table-wrapper">
                          <table>
                            <thead><tr><th>Specialità</th><th>Tempo</th></tr></thead>
                            <tbody>
                              {group.gare.map((g, j) => (
                                <tr key={j}>
                                  <td>{g.specialita}</td>
                                  <td className="tempo">{g.tempo}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

            </div>
          )}

          {profile?.error && (
            <div className="error-message" role="alert">
              <span>{profile.error}</span>
              <button className="retry-btn" onClick={() => selezionaAtleta(atleta)}>Riprova</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
